import L, { FeatureGroup, LayerGroup } from "leaflet";
import "leaflet.pattern/dist/leaflet.pattern-src.js";
import dayjs from "dayjs";
import "dayjs/locale/da";
import relativeTime from "dayjs/plugin/relativeTime";

// Project import
import { truncLongTextToHtml } from "utils/textModification";
import { beautifyNumber } from "utils/numberModification";
import { lightenDarkenColor } from "utils/colorModification";
import {
  removeFeatureFromLayerGroup,
  findFeatureFromId,
  createNewPattern,
  geoJsonCoordsToLatLngs,
  getPointInsideGeoJsonPolygon,
} from "./helperFunctions";
import CanvasLabelLayer, { CanvasLabelDatum, CanvasLabelLine } from "./Extensions/CanvasLabelLayer";
import Corridor from "./Extensions/Corridor";
import { unbindLayerTooltip } from "./tooltipLifecycle";

// Types
import { Feature, GeoJsonProperties, Polygon, Position } from "geojson";
import { AddRemoveVisibilityTypes } from "features/map/types/featureLayer";
import { getCommonElements, hash01 } from "utils/dataManipulation";
import {
  SPECIAL_STYLE_CATEGORIES,
  SPECIAL_STYLE_CATEGORIES_TYPE,
  TBD_SPECIES,
} from "config/config-general";
import { IconID } from "types/auth";
import { GeojsonLayerStyleOverwrites, SvgIconStyleObject } from "types/mapData";
import { FeatureStyleObject } from "types/firestoreFeatures";
import { getBufferPolygon } from "lib/turf/geoJsonHelperFunctions";

// Setup dayjs
dayjs.extend(relativeTime);
dayjs.locale("da");

interface GeoJsonLayerGroupProps {
  name: string;
  map: L.Map;
  setSelectedFeature: (feature: null | any, originCaller?: string) => void;
  getSpeciesColor: (species: string) => string;
  getLineStyle: (
    name?: string,
    styleObject?: FeatureStyleObject,
  ) => { color: string; dashArray: string | null; weight: number };
  getIcon: (iconId: IconID, svgStyleObject: SvgIconStyleObject) => string | null;
  getSpeciesNameAndAbbreviation: (species: string) => { name: string; abbreviation: string };
  getOverwrites?: () => GeojsonLayerStyleOverwrites | undefined;
  options?: GeoJsonLayerGroupOptions;
}

export interface GeoJsonLayerGroupOptions {
  globalEditMode?: boolean;
  pointsLayerMinZoom?: number;
  linesLayerMinZoom?: number;
  polygonsLayerMinZoom?: number;
  labelsLayerMinZoom?: number;
  labelsLayerZoomDependent?: boolean;
  pointSnapIgnore?: boolean;
  lineSnapIgnore?: boolean;
  polygonSnapIgnore?: boolean;
  labelSnapIgnore?: boolean;
  pointsPane?: string;
  linesPane?: string;
  linesAreaBearingPane?: string;
  lineBuffersPane?: string;
  polygonsPane?: string;
  polygonsAreaBearingPane?: string;
  labelsPane?: string;
  toolTipPane?: string;
  polygonNotSelectedLineColor?: string;
  polygonSelectedDashArray?: string;
  polygonNotSelectedDashArray?: string;
  polygonFillOpacity?: number;
  polygonLineWeight?: number;
  withToolTip?: boolean;
  featureClickEnabled?: boolean;
  labelFontSize?: number;
  labelBackgroundOpacity?: number;
  textPointFontSize?: number;
}

export function getPolygonPane(
  feature: { properties?: Record<string, unknown>; feature?: { properties?: Record<string, unknown> } },
  panes: {
    origins: string;
    background: string;
    areaBearing: string;
    normal: string;
  },
): string {
  const properties = feature.properties ?? feature.feature?.properties;
  if (properties?.originFeature) return panes.origins;
  // Program-defined polygons carry their species/type in mainSpecies. The form
  // persists normal placement for them, and this guard keeps stale data normal.
  if (properties?.renderPlacement === "background" && !properties?.mainSpecies) return panes.background;
  if (properties?.areaBearing) return panes.areaBearing;
  return panes.normal;
}

class GeoJsonLayerGroup {
  // --- Public parameters --- //
  public name: string;
  public selectedFeature: any;
  // --- Options --- //
  private _globalEditMode: boolean = false;
  private _pointsLayerMinZoom: number;
  private _linesLayerMinZoom: number;
  private _polygonsLayerMinZoom: number;
  private _labelsLayerZoomDependent: boolean = true;
  private _pointSnapIgnore: boolean;
  private _lineSnapIgnore: boolean;
  private _polygonSnapIgnore: boolean;
  private _labelSnapIgnore: boolean;
  private _pointsPane: string;
  private _pointsOriginsPane: string = "projectOriginsPointsPane";
  private _linesPane: string;
  private _linesAreaBearingPane: string = "linesAreaBearingPane";
  private _linesBuffersPane: string = "linesBuffersPane";
  private _linesOriginsPane: string = "projectOriginsLinesPane";
  private _polygonsPane: string = "polygonsPane";
  private _polygonsBackgroundPane: string = "polygonsBackgroundPane";
  private _polygonsAreaBearingPane: string = "polygonsAreaBearingPane";
  private _polygonsOriginsPane: string = "projectOriginsPolygonsPane";
  private _labelsPane: string;
  private _toolTipPane: string;
  private _polygonNotSelectedLineColor: string;
  private _polygonSelectedDashArray: string;
  private _polygonNotSelectedDashArray?: string;
  private _polygonFillOpacity: number;
  private _polygonLineWeight: number;
  private _polygonSelectedFillOpacity: number = 0;
  private _polygonSelectedLineWeight: number = 3;
  private _notInFilterOpacity: number = 0.3;
  private _transparent: boolean = false;
  private _darkTheme: boolean = false;
  private _filterIds: null | string[] = null;
  private _lineBufferSteps: number = 5; //3;
  private _lineBufferCorrectionFactorCoeffA: number = 0.0; // 0.00002;
  private _lineBufferCorrectionFactorCoeffB: number = 0.0; // 0.00007;
  private _labelFontSize: number = 10.5;
  private _labelBackgroundOpacity?: number = undefined;
  private _textPointFontSize: number = 12;
  // --- Internal parameters --- //
  private _map: L.Map;
  private _selectedFeatureId: string | null = null;
  private _pointsLayerActive: boolean = true;
  private _linesLayerActive: boolean = true;
  private _polygonsLayerActive: boolean = true;
  private _pointLabelsLayerActive: boolean = true;
  private _lineLabelsLayerActive: boolean = true;
  private _polygonLabelsLayerActive: boolean = true;
  private _pointLabelIdsByFeatureId = new Map<string, string[]>();
  private _lineLabelIdsByFeatureId = new Map<string, string[]>();
  private _polygonLabelIdsByFeatureId = new Map<string, string[]>();
  private _pointsTooltipActive: boolean = true;
  private _linesTooltipActive: boolean = true;
  private _polygonsTooltipActive: boolean = true;
  private _featureClickEnabled: boolean = true;
  private _cutDrawActive: boolean = false;
  private _cutPointerEvents = new Map<HTMLElement, string>();
  // --- Private Layers --- //
  private _originsPoints: L.GeoJSON;
  private _points: L.GeoJSON;
  private _originsLines: L.FeatureGroup;
  private _lines: L.FeatureGroup;
  private _lineBuffers: L.GeoJSON;
  private _originsPolygons: L.GeoJSON;
  private _polygons: L.GeoJSON;
  private _pointLabels: L.GeoJSON;
  private _lineLabels: L.GeoJSON;
  private _pointLabelCanvas: ReturnType<typeof CanvasLabelLayer>;
  private _lineLabelCanvas: ReturnType<typeof CanvasLabelLayer>;
  private _polygonLabelCanvas: ReturnType<typeof CanvasLabelLayer>;
  private _translations: { [key: string]: string };
  // --- Private functions --- //
  private _setSelectedFeature: (feature: null | any, originCaller?: string) => void;
  private _getSpeciesColor: (species: string, styleObject?: FeatureStyleObject) => string;
  private _getLineStyle: (
    name?: string,
    styleObject?: FeatureStyleObject,
  ) => { color: string; dashArray: string | null; weight: number };
  private _getIcon: (iconId: IconID, svgStyleObject: SvgIconStyleObject) => string | null;
  private _getSpeciesNameAndAbbreviation: (species: string) => {
    name: string;
    abbreviation: string;
  };
  private _getOverwrites?: () => GeojsonLayerStyleOverwrites | undefined;

  // --- Constructor --- //
  constructor({
    name,
    map,
    setSelectedFeature,
    getSpeciesColor,
    getIcon,
    getLineStyle,
    getSpeciesNameAndAbbreviation,
    getOverwrites,
    options,
  }: GeoJsonLayerGroupProps) {
    // set options
    this.name = name;
    this._map = map;
    this._pointsLayerMinZoom =
      options?.pointsLayerMinZoom !== undefined ? options.pointsLayerMinZoom : 15; //14
    this._linesLayerMinZoom =
      options?.linesLayerMinZoom !== undefined ? options.linesLayerMinZoom : 13;
    this._polygonsLayerMinZoom =
      options?.polygonsLayerMinZoom !== undefined ? options.polygonsLayerMinZoom : 8;
    this._labelsLayerZoomDependent =
      options?.labelsLayerZoomDependent !== undefined ? options.labelsLayerZoomDependent : true;
    this._pointSnapIgnore = options?.pointSnapIgnore ?? true;
    this._lineSnapIgnore = options?.lineSnapIgnore ?? false;
    this._polygonSnapIgnore = options?.polygonSnapIgnore ?? false;
    this._labelSnapIgnore = options?.labelSnapIgnore ?? true;
    this._pointsPane = options?.pointsPane || "pointsPane";
    this._linesPane = options?.linesPane || "linesPane";
    this._linesAreaBearingPane = options?.linesAreaBearingPane || "linesAreaBearingPane";
    this._linesBuffersPane = options?.lineBuffersPane || "linesBuffersPane";
    this._polygonsPane = options?.polygonsPane || "polygonsPane";
    this._polygonsAreaBearingPane = options?.polygonsAreaBearingPane || "polygonsAreaBearingPane";
    this._labelsPane = options?.labelsPane || "labelsPane";
    this._toolTipPane = options?.toolTipPane || "toolTipPane";
    this._polygonNotSelectedLineColor = options?.polygonNotSelectedLineColor || "#5a5a5a";
    this._polygonSelectedDashArray = options?.polygonSelectedDashArray || "1,0";
    this._polygonNotSelectedDashArray = options?.polygonNotSelectedDashArray || undefined;
    this._polygonFillOpacity = options?.polygonFillOpacity || 0.9;
    this._polygonLineWeight = options?.polygonLineWeight || 1;
    this._featureClickEnabled = options?.featureClickEnabled ?? true;
    this._labelFontSize = options?.labelFontSize || 10.5;
    this._labelBackgroundOpacity = options?.labelBackgroundOpacity;

    this._textPointFontSize = options?.textPointFontSize || 10.5; // 12
    this._translations = {
      type: "Type",
      area: "Area",
      length: "Length",
      species: "Species",
      year: "Year (age)",
      volume: "Volume",
    };
    // Call back functions
    this._setSelectedFeature = setSelectedFeature;
    this._getSpeciesColor = getSpeciesColor;
    this._getIcon = getIcon;
    this._getLineStyle = getLineStyle;
    this._getSpeciesNameAndAbbreviation = getSpeciesNameAndAbbreviation;
    this._getOverwrites = getOverwrites;

    // Init layers
    this._originsPoints = L.geoJSON(undefined, {
      pointToLayer: (feature, latlng) => this._setPointToLayer(feature, latlng),
      onEachFeature: (feature, layer) => this._onEachPointFeature(feature, layer),
      // @ts-expect-error
      layerGroupName: this.name,
      pane: this._pointsOriginsPane,
      snapIgnore: this._pointSnapIgnore,
    }).addTo(this._map);
    this._points = L.geoJSON(undefined, {
      pointToLayer: (feature, latlng) => this._setPointToLayer(feature, latlng),
      onEachFeature: (feature, layer) => this._onEachPointFeature(feature, layer),
      // @ts-expect-error
      layerGroupName: this.name,
      pane: this._pointsPane,
      snapIgnore: this._pointSnapIgnore,
    }).addTo(this._map);
    this._originsLines = new L.FeatureGroup(undefined, {
      // @ts-expect-error
      layerGroupName: this.name,
      snapIgnore: this._lineSnapIgnore,
    }).addTo(this._map);
    this._lines = new L.FeatureGroup(undefined, {
      // @ts-expect-error
      layerGroupName: this.name,
      snapIgnore: this._lineSnapIgnore,
    }).addTo(this._map);
    this._lineBuffers = L.geoJSON(undefined, {
      style: (feature) => this._setLineBufferFeatureStyle(feature),
      onEachFeature: (feature, layer) => this._onEachLineBufferFeature(feature, layer),
      // @ts-expect-error
      layerGroupName: this.name,
      pane: this._linesBuffersPane,
    });
    this._originsPolygons = L.geoJSON(undefined, {
      style: (feature) => this._setPolygonFeatureStyle(feature),
      onEachFeature: (feature, layer) => this._onEachPolygonFeature(feature, layer),
      // @ts-expect-error
      layerGroupName: this.name,
      pane: this._polygonsOriginsPane,
      snapIgnore: this._polygonSnapIgnore,
    }).addTo(this._map);
    this._polygons = L.geoJSON(undefined, {
      style: (feature) => this._setPolygonFeatureStyle(feature),
      onEachFeature: (feature, layer) => this._onEachPolygonFeature(feature, layer),
      // @ts-expect-error
      layerGroupName: this.name,
      pane: this._polygonsPane,
      snapIgnore: this._polygonSnapIgnore,
    }).addTo(this._map);
    this._pointLabels = L.geoJSON(undefined, {
      // @ts-expect-error
      layerGroupName: this.name,
      pane: this._labelsPane,
      snapIgnore: this._labelSnapIgnore,
    }).addTo(this._map);
    this._lineLabels = L.geoJSON(undefined, {
      // @ts-expect-error
      layerGroupName: this.name,
      pane: this._labelsPane,
      snapIgnore: this._labelSnapIgnore,
    }).addTo(this._map);

    // Labels Canvas layers
    this._pointLabelCanvas = CanvasLabelLayer({
      pane: this._labelsPane,
      showDuringZoom: false,
      zoomEndDebounceMs: 140,
      zoomingOpacity: 0.15,
    }).addTo(this._map);

    this._lineLabelCanvas = CanvasLabelLayer({
      pane: this._labelsPane,
      showDuringZoom: false,
      zoomEndDebounceMs: 140,
      zoomingOpacity: 0.15,
    }).addTo(this._map);

    this._polygonLabelCanvas = CanvasLabelLayer({
      pane: this._labelsPane,
      showDuringZoom: false,
      zoomEndDebounceMs: 140,
      zoomingOpacity: 0.15,
      // onClickId: (id) => {}, // TODO: Does not work as all click events are "stolen" by canvas layer
    }).addTo(this._map);

    // Set layer event listeners functionality
    if (this._featureClickEnabled) {
      this._points.on("click", (e) => this._pointsClickHandler(e));
      this._lines.on("click", (e) => this._linesClickHandler(e));
      this._polygons.on("click", (e) => this._polygonsClickHandler(e));
    }

    // Set map event listeners functionality
    this._map.on("zoomend", (e) => this._mapZoomEnd(e));
    this._map.on("pm:drawstart", (e) => this._drawStart(e));
    this._map.on("pm:drawend", (e) => this._drawEnd(e));
    this._map.on("dragstart", (e) => this._mapDragStart(e));
    this._map.on("dragend", (e) => this._mapDragEnd(e));

    // --- Initialize --- //
    this._initialize();
  }

  // --- Public functions --- //
  public setOptions(options: GeoJsonLayerGroupOptions): void {
    this._globalEditMode =
      options.globalEditMode !== undefined ? options.globalEditMode : this._globalEditMode;
    if (this._globalEditMode) {
      this._map.addLayer(this._lineBuffers);
      // Keep tooltip bindings intact while editing. Leaflet registers a focus
      // callback for each SVG path when a tooltip is bound; unbinding it while
      // a path is focused can leave that callback running with `_tooltip` set
      // to null. Closing the tooltip hides it without leaving a stale handler.
      if (this._pointsTooltipActive) this._closeTooltipLayer(this._points);
      if (this._linesTooltipActive) this._closeTooltipLayer(this._lines);
      if (this._polygonsTooltipActive) this._closeTooltipLayer(this._polygons);
    } else {
      this._map.removeLayer(this._lineBuffers);
      if (this._pointsTooltipActive) this._bindTooltipLayer(this._points);
      if (this._linesTooltipActive) this._bindTooltipLayer(this._lines);
      if (this._polygonsTooltipActive) this._bindTooltipLayer(this._polygons);
    }
  }

  public setTranslations(translations: { [key: string]: string }) {
    this._translations = {
      ...this._translations,
      ...translations,
    };
    if (this._pointsTooltipActive) {
      this.unbindTooltipLayer("points");
      this.bindTooltipLayer("points");
    }
    if (this._linesTooltipActive) {
      this.unbindTooltipLayer("lines");
      this.bindTooltipLayer("lines");
    }
    if (this._polygonsTooltipActive) {
      this.unbindTooltipLayer("polygons");
      this.bindTooltipLayer("polygons");
    }
  }

  public setSelectedFeatureId(id: string | null) {
    this._selectedFeatureId = id;
  }
  public setSelectedFeatureFromId(id: string) {
    // Set selected feature id
    this._selectedFeatureId = id;
    // Check polygons
    let feature = null;
    feature = findFeatureFromId(this._polygons, id);
    if (feature) {
      this._setSelectedFeature(feature, "GeoJsonLayerGroup-setSelectedFeatureFromId");
      return true;
    }
    // Check lines

    // Check points
    return false;
  }
  public setPreviousSelectedFeature(feature: null | any) {
    if (feature === null) {
      this._resetGlobalFeatureStyle(this.selectedFeature);
    }
    this.selectedFeature = feature;
  }

  public setFilterIds(ids: null | string[]) {
    this._filterIds = ids;
    // Set features styling based on filter
    if (this._filterIds) {
      // Check polygons
      if (this._polygons) {
        this._polygons.resetStyle();
        // @ts-ignore
        Object.values(this._polygons._layers).forEach((poly: any) => {
          if (
            poly.feature?.properties?.id &&
            !this._filterIds?.includes(poly.feature.properties.id)
          ) {
            if (this._transparent) {
              poly.setStyle(this._getTransparentStyle());
            } else {
              poly.setStyle(this._getFeatureNotInFilterStyle(poly));
            }
          } else {
            if (this._transparent) {
              poly.setStyle(this._getSelectedPolygonStyle(poly));
              poly.bringToFront();
            }
          }
        });
      }
      // TODO : Check lines
      // TODO : Check points
    } else {
      // Polygons
      if (this._polygons) {
        if (this._transparent) {
          this._polygons.setStyle(this._getTransparentStyle());
        } else {
          this._polygons.resetStyle();
        }
        if (this.selectedFeature) {
          // @ts-ignore
          Object.values(this._polygons._layers).forEach((poly: any) => {
            const selFeatId = this._getSelectedFeatureId(this.selectedFeature);
            if (
              poly.features &&
              poly.feature.properties &&
              poly.feature.properties.id === selFeatId
            ) {
              poly.setStyle(this._getSelectedPolygonStyle(poly));
            }
          });
        }
      }
      // TODO : Lines
      // TODO : Points
    }
  }

  public setTransparentMode(state: boolean) {
    this._transparent = state;
    if (this._transparent) {
      // Set polygon styles
      // @ts-ignore
      Object.values(this._polygons._layers).forEach((lay: any) => {
        // @ts-ignore
        lay.setStyle(this._getTransparentStyle(lay.feature));
      });
      // Set line styles
      // @ts-ignore
      Object.values(this._lines._layers).forEach((lay: any) => {
        lay.setStyle(this._getLineTransparentStyle());
      });
      // Set labels
      this._reinsertLabels("polygons");
      // Check for filter
      if (this._filterIds) {
        // @ts-ignore
        Object.values(this._polygons._layers).forEach((lay: any) => {
          if (this._filterIds?.includes(lay.feature.properties.id)) {
            lay.setStyle(this._getSelectedPolygonStyle(lay));
          }
        });
      }
      // Check for selected feature
      if (this.selectedFeature) {
        if (this.selectedFeature.feature) {
          if (
            this.selectedFeature.feature.geometry.type === "Polygon" ||
            this.selectedFeature.feature.geometry.type === "MultiPolygon"
          ) {
            try {
              // @ts-ignore
              this._polygons._layers[this.selectedFeature._leaflet_id].setStyle(
                this._getSelectedPolygonStyle(this.selectedFeature),
              );
            } catch (error) {
              console.warn("Can't find selected polygon in polygons!", error);
            }
          }
        } else if (
          this.selectedFeature.options &&
          ["LineString", "MultiLineString"].includes(this.selectedFeature.options.type)
        ) {
          // Set style for selected line
          // @ts-ignore
          try {
            // @ts-ignore
            this.selectedFeature.setStyle(this._getSelectedLineStyle(this.selectedFeature));
            // this._lines._layers[this.selectedFeature._leaflet_id].setStyle(this._getSelectedLineStyle(this.selectedFeature.options.mainSpecies, this.selectedFeature.options.styleObject))
          } catch (error) {
            console.warn("Can't find selected line in lines!", error);
          }
        }
      }
    } else {
      // Reset polygon style
      this._polygons.resetStyle();
      // Reset line style
      // @ts-ignore
      Object.values(this._lines._layers).forEach((lay: any) => {
        lay.setStyle(this._getLineStyle(lay.options.mainSpecies, lay.options.styleObject));
      });
      // Set labels
      this._reinsertLabels("polygons");
      // Check for filter
      if (this._filterIds) {
        // @ts-ignore
        Object.values(this._polygons._layers).forEach((lay: any) => {
          if (!this._filterIds?.includes(lay.feature.properties.id)) {
            lay.setStyle(this._getFeatureNotInFilterStyle(lay));
          }
        });
      }
      // Check for selected feature
      if (this.selectedFeature) {
        if (this.selectedFeature.feature) {
          // Set style of selected polygon
          if (
            this.selectedFeature.feature.geometry.type === "Polygon" ||
            this.selectedFeature.feature.geometry.type === "MultiPolygon"
          ) {
            try {
              // @ts-ignore
              this._polygons._layers[this.selectedFeature._leaflet_id].setStyle(
                this._getSelectedPolygonStyle(this.selectedFeature),
              );
            } catch (error) {
              console.warn("Can't find selected polygon in polygons!", error);
            }
          }
        } else if (
          this.selectedFeature.options &&
          ["LineString", "MultiLineString"].includes(this.selectedFeature.options.type)
        ) {
          // Set style for selected line
          // @ts-ignore
          try {
            // @ts-ignore
            this.selectedFeature.setStyle(this._getSelectedLineStyle(this.selectedFeature));
            // this._lines._layers[this.selectedFeature._leaflet_id].setStyle(this._getSelectedLineStyle(this.selectedFeature.options.mainSpecies, this.selectedFeature.options.styleObject))
          } catch (error) {
            console.warn("Can't find selected line in lines!", error);
          }
        }
      } else if (this._selectedFeatureId) {
        // Set style of selected polygon
        // @ts-ignore
        Object.values(this._polygons._layers).forEach((lay: any) => {
          if (lay.feature.properties.id === this._selectedFeatureId) {
            lay.setStyle(this._getSelectedPolygonStyle(lay));
          }
        });
        // Set style for line
      }
    }
  }

  public setDarkTheme(state: boolean) {
    this._darkTheme = state;
    this.setTransparentMode(this._transparent);
    this._setPointsIcon();
  }

  public addFeatureToLayer(feature: Feature): void | Error {
    // If filter is set, add newly added feature to filter
    //  if (
    //    feature.properties &&
    //    feature.properties.id &&
    //    this._filterIds &&
    //    !this._filterIds.includes(feature.properties.id)
    //  ) {
    //    this._filterIds.push(feature.properties.id);
    //  }

    const { geometry } = feature;
    switch (geometry.type) {
      case "Point":
      case "MultiPoint":
        this._addFeatureToPointLayer(feature);
        return;
      case "LineString":
      case "MultiLineString":
        this._addFeatureToLineLayer(feature);
        return;
      case "Polygon":
      case "MultiPolygon":
        this._addFeatureToPolygonLayer(feature);
        return;
      default:
        throw new Error("Error: Feature type is unknown! " + geometry.type);
    }
  }

  public removeFeatureFromLayer(featureId: string, featureType: string) {
    switch (featureType) {
      case "Point":
      case "MultiPoint":
        this._removeFeatureFromPointLayer(featureId);
        return;
      case "LineString":
      case "MultiLineString":
        this._removeFeatureFromLineLayer(featureId);
        return;
      case "Polygon":
      case "MultiPolygon":
        this._removeFeatureFromPolygonLayer(featureId);
        return;
      default:
        throw new Error("Error: Feature type is unknown! " + featureType);
    }
  }

  public removeFeature(featureId: string): boolean {
    if (this._removeFeatureFromPolygonLayer(featureId)) return true;
    if (this._removeFeatureFromLineLayer(featureId)) return true;
    if (this._removeFeatureFromPointLayer(featureId)) return true;
    return false;
  }

  public resetLayers(type: "all" | "points" | "lines" | "polygons" | "labels") {
    switch (type) {
      case "all":
        this._resetLayer(this._originsPoints);
        this._resetLayer(this._points);
        this._pointLabelCanvas.clear();
        this._pointLabelIdsByFeatureId.clear();
        this._resetLayer(this._originsLines);
        this._resetLayer(this._lines);
        this._resetLayer(this._lineBuffers);
        this._lineLabelCanvas.clear();
        this._lineLabelIdsByFeatureId.clear();
        this._resetLayer(this._originsPolygons);
        this._resetLayer(this._polygons);
        this._polygonLabelCanvas.clear();
        this._polygonLabelIdsByFeatureId.clear();
        break;
      case "points":
        this._resetLayer(this._originsPoints);
        this._resetLayer(this._points);
        this._pointLabelCanvas.clear();
        this._pointLabelIdsByFeatureId.clear();
        break;
      case "lines":
        this._resetLayer(this._originsLines);
        this._resetLayer(this._lines);
        this._resetLayer(this._lineBuffers);
        this._lineLabelCanvas.clear();
        this._lineLabelIdsByFeatureId.clear();
        break;
      case "polygons":
        this._resetLayer(this._originsPolygons);
        this._resetLayer(this._polygons);
        this._polygonLabelCanvas.clear();
        this._polygonLabelIdsByFeatureId.clear();
        break;
      case "labels":
        this._pointLabelCanvas.clear();
        this._pointLabelIdsByFeatureId.clear();
        this._lineLabelCanvas.clear();
        this._lineLabelIdsByFeatureId.clear();
        this._polygonLabelCanvas.clear();
        this._polygonLabelIdsByFeatureId.clear();
        break;
      default:
        this.resetLayers("all");
    }
  }

  public getActiveLayers(): string[] {
    let activeLayers = [];
    if (this._map.hasLayer(this._points)) activeLayers.push("points");
    if (this._map.hasLayer(this._lines)) activeLayers.push("lines");
    if (this._map.hasLayer(this._polygons)) activeLayers.push("polygons");
    if (this._map.hasLayer(this._pointLabelCanvas)) activeLayers.push("pointLabels");
    if (this._map.hasLayer(this._lineLabelCanvas)) activeLayers.push("lineLabels");
    if (this._map.hasLayer(this._polygonLabelCanvas)) activeLayers.push("polygonLabels");
    return activeLayers;
  }

  public getPolygonFeatureLayers(): L.Layer[] {
    return this._polygons.getLayers();
  }

  public addLayerVisibility(type: AddRemoveVisibilityTypes) {
    switch (type) {
      case "all":
        this._map.addLayer(this._points.bringToFront());
        this._map.addLayer(this._lines.bringToFront());
        this._map.addLayer(this._polygons.bringToFront());
        this._map.addLayer(this._pointLabelCanvas);
        this._map.addLayer(this._lineLabelCanvas);
        this._map.addLayer(this._polygonLabelCanvas);
        this._pointsLayerActive = true;
        this._linesLayerActive = true;
        this._polygonsLayerActive = true;
        this._pointLabelsLayerActive = true;
        this._lineLabelsLayerActive = true;
        this._polygonLabelsLayerActive = true;
        return;
      case "points":
        this._map.addLayer(this._points.bringToFront());
        this._pointsLayerActive = true;
        return;
      case "lines":
        this._map.addLayer(this._lines.bringToFront());
        this._linesLayerActive = true;
        return;
      case "polygons":
        this._map.addLayer(this._polygons.bringToFront());
        this._polygonsLayerActive = true;
        return;
      case "pointLabels":
        this._map.addLayer(this._pointLabelCanvas);
        this._pointLabelsLayerActive = true;
        return;
      case "lineLabels":
        this._map.addLayer(this._lineLabelCanvas);
        this._lineLabelsLayerActive = true;
        return;
      case "polygonLabels":
        this._map.addLayer(this._polygonLabelCanvas);
        this._polygonLabelsLayerActive = true;
        return;
      default:
        throw new Error("Type for add layer visibility not known! Input type: " + type);
    }
  }

  public removeLayerVisibility(type: AddRemoveVisibilityTypes) {
    switch (type) {
      case "all":
        this._map.removeLayer(this._points);
        this._map.removeLayer(this._lines);
        this._map.removeLayer(this._polygons);
        this._map.removeLayer(this._pointLabelCanvas);
        this._map.removeLayer(this._lineLabelCanvas);
        this._map.removeLayer(this._polygonLabelCanvas);
        this._pointsLayerActive = false;
        this._linesLayerActive = false;
        this._polygonsLayerActive = false;
        this._pointLabelsLayerActive = false;
        this._lineLabelsLayerActive = false;
        this._polygonLabelsLayerActive = false;
        return;
      case "points":
        this._map.removeLayer(this._points);
        this._pointsLayerActive = false;
        return;
      case "lines":
        this._map.removeLayer(this._lines);
        this._linesLayerActive = false;
        return;
      case "polygons":
        this._map.removeLayer(this._polygons);
        this._polygonsLayerActive = false;
        return;
      case "pointLabels":
        this._map.removeLayer(this._pointLabelCanvas);
        this._pointLabelsLayerActive = false;
        return;
      case "lineLabels":
        this._map.removeLayer(this._lineLabelCanvas);
        this._lineLabelsLayerActive = false;
        return;
      case "polygonLabels":
        this._map.removeLayer(this._polygonLabelCanvas);
        this._polygonLabelsLayerActive = false;
        return;
      case "labels":
        this._map.removeLayer(this._pointLabelCanvas);
        this._pointLabelsLayerActive = false;
        this._map.removeLayer(this._lineLabelCanvas);
        this._lineLabelsLayerActive = false;
        this._map.removeLayer(this._polygonLabelCanvas);
        this._polygonLabelsLayerActive = false;
        return;
      default:
        throw new Error("Type for remove layer visibility not known! Input type: " + type);
    }
  }

  public bindTooltipLayer(type: AddRemoveVisibilityTypes) {
    switch (type) {
      case "all":
        this._bindTooltipLayer(this._points);
        this._bindTooltipLayer(this._lines);
        this._bindTooltipLayer(this._polygons);
        this._pointsTooltipActive = true;
        this._linesTooltipActive = true;
        this._polygonsTooltipActive = true;
        return;
      case "points":
        this._bindTooltipLayer(this._points);
        this._pointsTooltipActive = true;
        return;
      case "lines":
        this._bindTooltipLayer(this._lines);
        this._linesTooltipActive = true;
        return;
      case "polygons":
        this._bindTooltipLayer(this._polygons);
        this._polygonsTooltipActive = true;
        return;
    }
  }

  public unbindTooltipLayer(type: AddRemoveVisibilityTypes) {
    switch (type) {
      case "all":
        this._unbindTooltipLayer(this._points);
        this._unbindTooltipLayer(this._lines);
        this._unbindTooltipLayer(this._polygons);
        this._pointsTooltipActive = false;
        this._linesTooltipActive = false;
        this._polygonsTooltipActive = false;
        return;
      case "points":
        this._unbindTooltipLayer(this._points);
        this._pointsTooltipActive = false;
        return;
      case "lines":
        this._unbindTooltipLayer(this._lines);
        this._linesTooltipActive = false;
        return;
      case "polygons":
        this._unbindTooltipLayer(this._polygons);
        this._polygonsTooltipActive = false;
        return;
    }
  }

  // --- Event listeners --- //
  // Layers
  private _pointsClickHandler(e: any) {
    // Check if edit mode is enabled - if so return
    if (this._globalEditMode) return;
    // Reset style of previous selected feature (checking globally)
    this._resetGlobalFeatureStyle(this.selectedFeature);
    // Set selected feature globally
    const selFeatId = this._getSelectedFeatureId(this.selectedFeature);
    // If the same as previous deselect feature
    if (selFeatId === e.layer.feature.properties.id) {
      this._setSelectedFeature(null, "GeoJsonLayerGroup-_pointsClickHandler");
    } else {
      this._setSelectedFeature(e.layer, "GeoJsonLayerGroup-_pointsClickHandler");
    }
    if (e.originalEvent) e.originalEvent.view.L.DomEvent.stopPropagation(e);
  }
  private _linesClickHandler(e: any) {
    // Check if edit mode is enabled - if so return
    if (this._globalEditMode) return;
    // Reset style of previous selected feature (checking globally)
    this._resetGlobalFeatureStyle(this.selectedFeature);
    // Set style of selected feature and move to front
    e.layer.setStyle(this._getSelectedLineStyle(e.layer));
    e.layer.bringToFront();
    // Set selected feature globally
    const selFeatId = this._getSelectedFeatureId(this.selectedFeature);
    // // If the same as previous deselect feature
    if (selFeatId === e.layer.options.id) {
      this._setSelectedFeature(null, "GeoJsonLayerGroup-_linesClickHandler");
    } else {
      this._setSelectedFeature(e.layer, "GeoJsonLayerGroup-_linesClickHandler");
    }
    if (e.originalEvent) e.originalEvent.view.L.DomEvent.stopPropagation(e);
  }
  private _polygonsClickHandler(e: any) {
    // Check if edit mode is enabled - if so return
    if (this._globalEditMode) return;
    // Reset style of previous selected feature (checking globally)
    this._resetGlobalFeatureStyle(this.selectedFeature);
    // Set style of selected feature and move to front
    e.layer.setStyle(this._getSelectedPolygonStyle(e.layer));
    e.layer.bringToFront();
    // Set selected feature globally
    const selFeatId = this._getSelectedFeatureId(this.selectedFeature);
    // If the same as previous deselect feature
    if (selFeatId === e.layer.feature.properties.id) {
      this._setSelectedFeature(null, "GeoJsonLayerGroup-_polygonsClickHandler");
    } else {
      this._setSelectedFeature(e.layer, "GeoJsonLayerGroup-_polygonsClickHandler");
    }
    if (e.originalEvent) e.originalEvent.view.L.DomEvent.stopPropagation(e);
  }
  private _labelsClickHandler(e: any) {
    // Check if edit mode is enabled - if so return
    if (this._globalEditMode) return;
    // Else find parent layer
    let parentLayer = null;
    this._map.eachLayer((layer) => {
      // @ts-ignore
      if (layer.feature && layer.feature.properties.id === e.layer.options.id) {
        parentLayer = layer;
      } else if (
        layer.options &&
        // @ts-expect-error
        layer.options.id === e.layer.options.id &&
        // @ts-expect-error
        layer.options.type !== "lineLabel"
      ) {
        // @ts-ignore
        parentLayer = layer;
      }
    });
    // TODO : Find more eccient way to find parent layer
    // @ts-ignore
    if (parentLayer && parentLayer.feature) {
      // @ts-ignore
      switch (parentLayer.feature.geometry.type) {
        case "Point":
        case "MultiPoint":
          this._pointsClickHandler({ layer: parentLayer });
          return;
        case "LineString":
        case "MultiLineString":
          this._linesClickHandler({ layer: parentLayer });
          return;
        case "Polygon":
        case "MultiPolygon":
          this._polygonsClickHandler({ layer: parentLayer });
          return;
        default:
          console.warn("Parent layer geometry type not supported");
          return;
      }
    } else if (parentLayer) {
      // @ts-expect-error
      switch (parentLayer.options.type) {
        case "Point":
        case "MultiPoint":
          this._pointsClickHandler({ layer: parentLayer });
          return;
        case "LineString":
        case "MultiLineString":
          this._linesClickHandler({ layer: parentLayer });
          return;
        case "Polygon":
        case "MultiPolygon":
          this._polygonsClickHandler({ layer: parentLayer });
          return;
        default:
          console.warn("Parent layer geometry type not supported");
          return;
      }
    } else {
      console.warn("Could not find label parent layer!");
    }
    e.stopPropagation();
  }
  // Map
  private _mapZoomEnd(e: any) {
    const zoomLevel = this._map.getZoom();
    // --- Layers zoom dependency --- //
    // Points
    if (zoomLevel <= this._pointsLayerMinZoom) {
      this._map.removeLayer(this._points);
    } else {
      if (this._pointsLayerActive) {
        this._map.addLayer(this._points);
      }
      if (this._pointLabelsLayerActive) {
        this._map.addLayer(this._pointLabelCanvas);
      }
    }
    // Lines
    if (zoomLevel <= this._linesLayerMinZoom) {
      this._map.removeLayer(this._lines);
    } else {
      if (this._linesLayerActive) {
        this._map.addLayer(this._lines);
      }
      if (this._lineLabelsLayerActive) {
        this._map.addLayer(this._lineLabelCanvas);
      }
    }
    // Polygons
    if (zoomLevel <= this._polygonsLayerMinZoom) {
      this._map.removeLayer(this._polygons);
    } else {
      if (this._polygonsLayerActive) {
        this._map.addLayer(this._polygons);
      }
      if (this._polygonLabelsLayerActive) {
        this._map.addLayer(this._polygonLabelCanvas);
      }
    }
  }
  private _mapDragStart(e: any) {
    // Existing geometry stays non-interactive while a cut is being drawn.
    // Do not unbind its tooltips in that window (see _drawStart).
    if (this._cutDrawActive) return;
    // unbind tooltips
    if (this._pointsTooltipActive) this._unbindTooltipLayer(this._points);
    if (this._linesTooltipActive) this._unbindTooltipLayer(this._lines);
    if (this._polygonsTooltipActive) this._unbindTooltipLayer(this._polygons);
  }
  private _mapDragEnd(e: any) {
    // bind tooltips again
    if (this._globalEditMode) return;
    if (this._pointsTooltipActive) this._bindTooltipLayer(this._points);
    if (this._linesTooltipActive) this._bindTooltipLayer(this._lines);
    if (this._polygonsTooltipActive) this._bindTooltipLayer(this._polygons);
  }

  // PM
  private _drawStart(e: any) {
    // Cut mode starts by clicking an existing SVG polygon. Unbinding its
    // tooltip here can leave Leaflet's DOM focus handler running briefly with
    // a null tooltip, causing "Cannot set properties of null (_source)" on
    // the first cut vertex. Keep existing tooltips bound for Cut only.
    this._cutDrawActive = e.shape === "Cut";
    if (this._cutDrawActive) {
      this._setCutFeaturePointerEvents(false);
    } else {
      if (this._pointsTooltipActive) this._unbindTooltipLayer(this._points);
      if (this._linesTooltipActive) this._unbindTooltipLayer(this._lines);
      if (this._polygonsTooltipActive) this._unbindTooltipLayer(this._polygons);
    }
    // remove click handlers
    this._points.off("click");
    this._lines.off("click");
    this._polygons.off("click");
    this._pointLabels.off("click");
    this._lineLabels.off("click");
  }
  private _drawEnd(e: any) {
    // Tooltips were never unbound for Cut, so do not bind a second set.
    if (e.shape === "Cut") {
      this._cutDrawActive = false;
      this._setCutFeaturePointerEvents(true);
    } else {
      if (this._pointsTooltipActive) this._bindTooltipLayer(this._points);
      if (this._linesTooltipActive) this._bindTooltipLayer(this._lines);
      if (this._polygonsTooltipActive) this._bindTooltipLayer(this._polygons);
    }
    // Restore feature selection only for layers that support feature clicks.
    if (this._featureClickEnabled) {
      this._points.on("click", (e) => this._pointsClickHandler(e));
      this._lines.on("click", (e) => this._linesClickHandler(e));
      this._polygons.on("click", (e) => this._polygonsClickHandler(e));
      this._pointLabels.on("click", (e) => this._labelsClickHandler(e));
      this._lineLabels.on("click", (e) => this._labelsClickHandler(e));
    }
  }

  /**
   * Geoman determines snapping from its layer registry, so map feature SVGs
   * do not need pointer interaction while the cut polygon is drawn. Disabling
   * it prevents the pointer from opening a tooltip after its snapped vertex
   * has moved away from the pointer location.
   */
  private _setCutFeaturePointerEvents(restore: boolean) {
    if (restore) {
      this._cutPointerEvents.forEach((pointerEvents, element) => {
        element.style.pointerEvents = pointerEvents;
      });
      this._cutPointerEvents.clear();
      return;
    }

    const disableLayerPointerEvents = (layer: L.Layer) => {
      if (layer instanceof L.LayerGroup) {
        layer.eachLayer(disableLayerPointerEvents);
        return;
      }
      const element = (layer as any).getElement?.() as HTMLElement | null;
      if (!element || this._cutPointerEvents.has(element)) return;

      this._cutPointerEvents.set(element, element.style.pointerEvents);
      element.style.pointerEvents = "none";
    };

    disableLayerPointerEvents(this._points);
    disableLayerPointerEvents(this._lines);
    disableLayerPointerEvents(this._polygons);
  }

  // --- Init functions --- //
  private _initialize() {
    this.removeLayerVisibility("all");
    this.bindTooltipLayer("all");
    this._pointsLayerActive = true;
    this._linesLayerActive = true;
    this._polygonsLayerActive = true;
    this._pointLabelsLayerActive = true;
    this._lineLabelsLayerActive = true;
    this._polygonLabelsLayerActive = true;
  }

  // --- Private functions --- //
  private _mkCanvasDatum(args: {
    id: string;
    placement: L.LatLngExpression;
    lines: CanvasLabelLine[];
    minZoom: number;
    fontWeight?: number;
    backgroundAlpha: number;
    angleRad?: number;
    offsetYPx?: number;
  }): CanvasLabelDatum {
    return {
      id: args.id,
      latlng: args.placement,
      lines: args.lines,
      minZoom: args.minZoom,
      fontSizePx: this._labelFontSize,
      fontWeight: args.fontWeight ?? 400,
      color: "black",
      backgroundAlpha: args.backgroundAlpha,
      paddingPx: 2,
      lineGapPx: 1,
      borderRadiusPx: 2,
      angleRad: args.angleRad,
      offsetYPx: args.offsetYPx ?? 0,
    };
  }
  // - On each layer - //
  // Points
  private _setPointToLayer(point: Feature, latlng: L.LatLng) {
    // Get marker icon based on poitn type
    let iconId = "default";
    if (point.properties?.styleObject?.pointIcon) {
      iconId = point.properties.styleObject.pointIcon;
    }
    const icon = this._getMarkerIcon(point.properties?.id, iconId, point.properties?.title);
    const marker = L.marker(latlng, { icon: icon, pane: this._pointsPane });
    // Tag layer
    const id = point.properties?.id as string;
    Object.assign((marker as any).options, {
      id,
      layerGroupName: this.name,
      role: "geometry",
      type: point.geometry.type,
    });
    return marker;
  }

  private _onEachPointFeature(feature: Feature, layer: L.Layer) {
    if (feature.geometry.type === "MultiPoint") {
      // @ts-ignore
      Object.values(layer._layers).forEach((lay: L.Layer) => {
        // @ts-ignore
        lay.feature = layer.feature;
        // Bind tooltip
        if (this._pointsTooltipActive && !this._globalEditMode) this._bindTooltipLayer(lay);
        // Add label
        // if (this._pointLabelsLayerActive)
        this._addPointLabel(lay);
      });
    } else {
      // Bind tooltip
      if (this._pointsTooltipActive && !this._globalEditMode) this._bindTooltipLayer(layer);
      // Add label
      // if (this._pointLabelsLayerActive)
      this._addPointLabel(layer);
    }
    // Check if feature is the same as selected feature and set style accordingly
    if (this.selectedFeature) {
      const selFeatId = this._getSelectedFeatureId(this.selectedFeature);
      if (selFeatId === feature?.properties?.id) {
        this._setSelectedFeature(layer, "GeoJsonLayerGroup-_onEachPointFeature");
      }
    }
    // Check for selected feature id from deep linking
    if (this._selectedFeatureId && this._selectedFeatureId === feature?.properties?.id) {
      this._setSelectedFeature(layer, "GeoJsonLayerGroup-_onEachPointFeature");
    }
  }

  // Lines
  private _setLineBufferFeatureStyle(feature: any) {
    return {
      color: feature.properties?.color || "red",
      fillColor: feature.properties?.color || "red",
      fillOpacity: 0,
      opacity: 0,
      pane: this._linesBuffersPane,
      // propagate tags onto the rendered layer as options as well
      id: feature.properties?.id,
      layerGroupName: feature.properties?.layerGroupName,
      role: "buffer",
      type: "Polygon",
    };
  }
  private _onEachLineBufferFeature(feature: any, layer: L.Layer) {}
  private _addFeatureToLinesLayer(feature: Feature) {
    // Get coordinates as latlngs
    const coords = geoJsonCoordsToLatLngs(feature);
    // Get style properties
    const styleProps = this._getLineFeatureStyle(feature);
    const lineWeight = styleProps.weight ? styleProps.weight / 2 : 0.5;
    // Get pane
    let pane = this._linesPane;
    if (feature.properties?.areaBearing) {
      pane = this._linesAreaBearingPane;
    }
    // Create corridor
    const corridor = Corridor(coords, {
      ...feature.properties,
      pane,
      corridor: lineWeight,
      color: styleProps.color,
      dashArray: styleProps.dashArray,
      originalColor: styleProps.color,
      lineCap: "butt",
      // Tag layer
      id: feature.properties?.id,
      layerGroupName: this.name,
      role: "geometry",
      type: feature.geometry.type,
    });
    // Add to layer
    this._lines.addLayer(corridor);
    // Bind tooltip
    if (this._linesTooltipActive && !this._globalEditMode) this._bindTooltipLayer(corridor);
    this._addLineLabel(corridor);
    // Check if feature is the same as selected feature and set style accordingly
    if (this.selectedFeature && feature.properties?.id === this.selectedFeature.options.id) {
      this._setSelectedFeature(corridor, "GeoJsonLayerGroup-_addFeatureToLinesLayer");
    }
    // Check for selected feature id from deep linking
    if (this._selectedFeatureId && this._selectedFeatureId === feature.properties?.id) {
      this._setSelectedFeature(corridor, "GeoJsonLayerGroup-_addFeatureToLinesLayer");
    }
    // Check for area bearing and insert polygon if so
    const buffer = getBufferPolygon(
      feature,
      lineWeight * 0.001 -
        (lineWeight * this._lineBufferCorrectionFactorCoeffA +
          this._lineBufferCorrectionFactorCoeffB),
      this._lineBufferSteps,
    );
    buffer.properties = {
      ...(buffer.properties || {}),
      color: styleProps.color,
      id: feature.properties?.id,
      layerGroupName: this.name,
      role: "buffer",
      type: "Polygon",
    };
    this._lineBuffers.addData(buffer);
  }

  // Polygons
  private _setPolygonFeatureStyle(feature: any) {
    // Check for special pattern
    const pattern = this._getPattern(feature);
    const section =
      feature.properties && feature.properties.section ? feature.properties.section.join("") : "";
    const mainSpecies =
      feature.properties && feature.properties.mainSpecies ? feature.properties.mainSpecies : "";
    const styleObject =
      feature.properties && feature.properties.styleObject ? feature.properties.styleObject : null;
    // Set polygon style if no main species and no section
    let fillOpacity = this._polygonFillOpacity;
    let color = this._polygonNotSelectedLineColor;
    let weight = this._polygonLineWeight;
    let fillColor = this._getSpeciesColor(mainSpecies, styleObject);
    const pane = this._getPolygonPane(feature);
    if (mainSpecies === "" && styleObject) {
      if (styleObject.polygonStyle && styleObject.polygonStyle === "Transparent") {
        fillOpacity = 0;
        color = fillColor;
        weight = 2;
      }
    }
    return {
      color,
      fillColor: fillColor,
      fillOpacity,
      weight,
      dashArray: this._polygonNotSelectedDashArray,
      fillPattern: pattern,
      pane,
    };
  }
  private _getPolygonPane(feature: any): string {
    return getPolygonPane(feature, {
      origins: this._polygonsOriginsPane,
      background: this._polygonsBackgroundPane,
      areaBearing: this._polygonsAreaBearingPane,
      normal: this._polygonsPane,
    });
  }
  private _onEachPolygonFeature(feature: Feature, layer: L.Layer) {
    // Tag layer
    Object.assign((layer as any).options, {
      id: feature.properties?.id,
      layerGroupName: this.name,
      role: feature.properties?.originFeature ? "originFeature" : "geometry",
      type: feature.geometry.type,
    });
    // Bind tooltip
    if (this._polygonsTooltipActive && !this._globalEditMode) this._bindTooltipLayer(layer);
    // Set label
    this._addPolygonLabel(layer, { transparent: this._transparent });
    // Check for selected feature id from deep linking
    if (this._selectedFeatureId && this._selectedFeatureId === feature.properties?.id) {
      this._setSelectedFeature(layer, "GeoJsonLayerGroup-_onEachPolygonFeature");
      //@ts-ignore
      layer.setStyle(this._getSelectedPolygonStyle(layer));
    }
    // Check if feature is the same as selected feature and set style accordingly
    let selectedFeatureProps = null;
    if (this.selectedFeature) {
      if (this.selectedFeature.feature) {
        selectedFeatureProps = this.selectedFeature.feature.properties;
      } else {
        selectedFeatureProps = this.selectedFeature.options;
      }
    }
    if (selectedFeatureProps && feature.properties?.id === selectedFeatureProps.id) {
      this._setSelectedFeature(layer, "GeoJsonLayerGroup-_onEachPolygonFeature");
      //@ts-ignore
      layer.setStyle(this._getSelectedPolygonStyle(layer));
    }
    // Check if feature is in filter
    if (this._filterIds) {
      if (feature.properties?.id && !this._filterIds.includes(feature.properties.id)) {
        //@ts-ignore
        layer.setStyle(this._getFeatureNotInFilterStyle(layer));
      }
    }
    // Check if transparent mode
    if (this._transparent) {
      //@ts-ignore
      layer.setStyle(this._getTransparentStyle(feature));
    }
  }

  // --- Utility functions --- //
  private _getSelectedFeatureId(feat: any) {
    if (!feat) return null;
    if (feat.feature) {
      return feat.feature.properties.id;
    } else if (feat.options) {
      return feat.options.id;
    }
    return null;
  }

  private _resetLayer(layer: LayerGroup): void | Error {
    layer.clearLayers();
  }

  private _setPointsIcon() {
    this._points.eachLayer((lay) => {
      // @ts-ignore
      const { properties } = lay.feature;
      let iconId = "default";
      if (properties && properties.styleObject && properties.styleObject.pointIcon) {
        iconId = properties.styleObject.pointIcon;
      }
      const newIcon = this._getMarkerIcon(properties?.id, iconId, properties?.title);
      // @ts-ignore
      lay.setIcon(newIcon);
    });
  }

  private _removePointCanvasLabelsForFeatureId(featureId: string) {
    const ids = this._pointLabelIdsByFeatureId.get(featureId);
    if (ids) for (const id of ids) this._pointLabelCanvas.removeId(id);
    this._pointLabelIdsByFeatureId.delete(featureId);
  }

  private _registerPointCanvasLabelIds(featureId: string, ids: string[]) {
    this._pointLabelIdsByFeatureId.set(featureId, ids);
  }

  private _removeLineCanvasLabelsForFeatureId(featureId: string) {
    const ids = this._lineLabelIdsByFeatureId.get(featureId);
    if (ids) {
      for (const id of ids) this._lineLabelCanvas.removeId(id);
    }
    this._lineLabelIdsByFeatureId.delete(featureId);
  }

  private _registerLineCanvasLabelIds(featureId: string, ids: string[]) {
    this._lineLabelIdsByFeatureId.set(featureId, ids);
  }

  private _removePolygonCanvasLabelsForFeatureId(featureId: string) {
    const ids = this._polygonLabelIdsByFeatureId.get(featureId);
    if (ids) {
      for (const id of ids) this._polygonLabelCanvas.removeId(id);
    }
    this._polygonLabelIdsByFeatureId.delete(featureId);
  }

  private _registerPolygonCanvasLabelIds(featureId: string, ids: string[]) {
    this._polygonLabelIdsByFeatureId.set(featureId, ids);
  }

  private _addPointLabel(layer: L.Layer, options?: { pane?: string }) {
    const pane = options?.pane || this._labelsPane;
    // Unpack parameters
    // @ts-ignore
    // Unpack properties
    // @ts-ignore
    const properties = layer.feature?.properties ?? layer.options;
    if (!properties) return;
    // skip "Text icon" points (matches your previous behavior)
    if (
      properties.styleObject?.pointIcon &&
      String(properties.styleObject.pointIcon).toLowerCase() === "text"
    ) {
      return;
    }
    const featureId = properties.id as string;
    if (!featureId) return;
    // title/section
    let pointId = properties.title ? properties.title : "";
    if (!pointId && properties.section) pointId = properties.section.join("");
    if (!pointId) return;
    // replace old
    this._removePointCanvasLabelsForFeatureId(featureId);
    const createdIds: string[] = [];

    // activity stacking
    const activity = !!(properties.activityId && !properties.originFeature);
    const offsetYPx = activity ? 47 : 25;

    // background alpha (same override semantics)
    let backgroundAlpha = 0.5;
    if (
      this._labelBackgroundOpacity !== undefined &&
      this._labelBackgroundOpacity >= 0 &&
      this._labelBackgroundOpacity <= 1
    ) {
      backgroundAlpha = this._labelBackgroundOpacity!;
    }

    // if filtered out, fade background
    if (this._filterIds && featureId && !this._filterIds.includes(featureId)) {
      backgroundAlpha = Math.min(backgroundAlpha, 0.2);
    }

    const z = this._labelsLayerZoomDependent
      ? this._pointsLayerMinZoom + 1
      : this._pointsLayerMinZoom;

    const minZoom = z + 1; // canvas uses `zoom >= minZoom` and your semantics are `zoom > zoomLevel`

    const lines: CanvasLabelLine[] = [
      {
        text: pointId,
        zoomLevel: z,
        fontSizePx: this._labelFontSize,
        fontWeight: activity ? 700 : 400,
        color: "black",
      },
    ];

    // placement
    // @ts-ignore
    const latlng: L.LatLng | undefined = layer.getLatLng?.() ?? layer._latlng;
    if (!latlng) return;

    const id = featureId; // single label per point
    createdIds.push(id);

    this._pointLabelCanvas.upsert(
      this._mkCanvasDatum({
        id,
        placement: latlng,
        lines,
        minZoom,
        fontWeight: activity ? 700 : 400,
        backgroundAlpha,
        offsetYPx,
      }),
    );

    this._registerPointCanvasLabelIds(featureId, createdIds);

    // Check for activity
    // let activity = false;
    // if (properties.activityId && !properties.originFeature) activity = true;

    // Set label content
    // const pointId = properties.title ? properties.title : properties.section.join("");
    // let text = `<p style="white-space: nowrap; position: relative;">
    //      <span style="position: absolute; top: ${
    //        activity ? "19px" : "0px"
    //      }; color: black; font-weight: ${
    //   activity ? "700" : "400"
    // }; background-color: ${bgColor}; padding: 0 2px; border-radius: 2px">
    //         ${pointId}
    //      </span>
    //   </p>`;

    // const zoomLevel = this._labelsLayerZoomDependent
    //   ? this._pointsLayerMinZoom + 1
    //   : this._pointsLayerMinZoom; //15;
    // @ts-ignore
    // LabelsMarker(layer._latlng, {
    //   ...properties,
    //   type: "pointLabel",
    //   pane: pane,
    //   snapIgnore: true,
    //   role: "label",
    //   layerGroupName: this.name,
    //   zoomContent: [{ type: "point", level: zoomLevel, text: text, fontSize: this._labelFontSize }],
    // }).addTo(this._pointLabels);
  }

  private _addLineLabel(
    layer: L.Layer,
    options?: { pane?: string; resolution?: number; offset?: number },
  ) {
    const resolutionMultiline = 4; // Min 1s
    const resolution = options?.resolution || 10; // 6
    const maxLabelsPerLine = 5;
    const minSpacingM = 150;

    // Unpack parameters
    let coordinates = [];
    let type = "";
    let properties: any = {};
    // @ts-ignore
    if (layer.feature) {
      // @ts-ignore
      coordinates = layer.feature!.geometry.coordinates;
      // @ts-ignore
      type = layer.feature.geometry.type;
      // @ts-ignore
      properties = layer.feature.properties;
    } else {
      // @ts-ignore
      const feat = layer.toGeoJSON();
      coordinates = feat.geometry.coordinates;
      type = feat.geometry.type;
      // @ts-ignore
      properties = { ...layer.options };
    }
    // Check for feature id
    const featureId = properties?.id as string;
    if (!featureId) return;
    const createdIds: string[] = [];
    // Replacing old labels for this feature
    this._removeLineCanvasLabelsForFeatureId(featureId);
    // Dynamically set label offset (both line and multi line)
    // Line id
    let lineId = properties.section ? properties.section.join("") : "";
    if (lineId === "") lineId = properties.title ? properties.title : "";
    // Insert no label if no line id
    if (lineId === "") return;

    // Check for activity
    let activity = false;
    if (properties.activityId && !properties.originFeature) activity = true;

    // Set label background opacity
    // Check if option overwrite is active
    let backgroundAlpha = 0.5;
    if (
      this._labelBackgroundOpacity !== undefined &&
      this._labelBackgroundOpacity >= 0 &&
      this._labelBackgroundOpacity <= 1
    ) {
      backgroundAlpha = this._labelBackgroundOpacity!;
    }

    // zoom semantics: show when mapZoom > zoomLevel
    const z = this._labelsLayerZoomDependent
      ? this._linesLayerMinZoom + 2
      : this._linesLayerMinZoom;

    // Activity stacking: shift whole label down
    const offsetYPx = activity ? 30 : 0; // old HTML used top: 19px; start here; tune if needed
    const minZoom = z + 1; // because draw condition is `zoom > zoomLevel`

    const lines: CanvasLabelLine[] = [];
    lines.push({
      text: lineId,
      zoomLevel: z,
      fontSizePx: this._labelFontSize,
      fontWeight: activity ? 700 : 400,
      color: "black",
    });
    if (activity) {
      lines.push({
        text: beautifyNumber(properties.featureMetric, 2) + " m",
        zoomLevel: z,
        fontSizePx: this._labelFontSize,
        fontWeight: 400,
        color: "black",
      });
    }

    if (type === "MultiLineString") {
      for (let index = 0; index < coordinates.length; index = index + resolutionMultiline) {
        const line = coordinates[index];
        const latLngs = (line as any[]).map((c) => L.latLng(c[1], c[0]));
        const samples = this._sampleLineByDistance(latLngs, {
          maxLabels: maxLabelsPerLine,
          minSpacingM: minSpacingM,
          seed: `${featureId}__${index ?? ""}__${lineId}`,
        });
        let partIdx = 0;
        for (const s of samples) {
          const partId = `${featureId}__${index}__${partIdx}`;
          createdIds.push(partId);
          this._lineLabelCanvas.upsert(
            this._mkCanvasDatum({
              id: partId,
              placement: s.latlng,
              lines,
              minZoom,
              fontWeight: activity ? 700 : 400,
              backgroundAlpha,
              offsetYPx,
            }),
          );
          partIdx++;
        }
      }
    } else {
      const latLngs = (coordinates as any[]).map((c) => L.latLng(c[1], c[0]));
      const samples = this._sampleLineByDistance(latLngs, {
        maxLabels: maxLabelsPerLine,
        minSpacingM: minSpacingM,
        seed: `${featureId}__${lineId}`,
      });
      let partIdx = 0;
      for (const s of samples) {
        const partId = `${featureId}__${partIdx}`;
        createdIds.push(partId);
        this._lineLabelCanvas.upsert(
          this._mkCanvasDatum({
            id: partId,
            placement: s.latlng,
            lines,
            minZoom,
            fontWeight: activity ? 700 : 400,
            backgroundAlpha,
            offsetYPx,
          }),
        );
        partIdx++;
      }
    }
    this._registerLineCanvasLabelIds(featureId, createdIds);
  }

  private _addPolygonLabel(
    layer: L.Layer,
    options?: { pane?: string; transparent?: boolean; type?: string },
  ) {
    const overwrites = this._getOverwrites ? this._getOverwrites() : null;
    // Set options
    const transparent = options?.transparent || false;
    // Unpack properties
    // @ts-ignore
    const { properties } = (layer as any).feature ?? {};
    if (!properties?.id) return;

    const featureId = properties.id as string;
    const createdIds: string[] = [];
    // Replace any old label parts for this feature id (e.g. geometry changed)
    this._removePolygonCanvasLabelsForFeatureId(featureId);
    // --- Zoom level 1 --- //
    // Litra id
    let line1 = "";
    if (properties && properties.section) {
      line1 = properties.section.join("");
    } else if (properties && properties.name) {
      line1 = properties.name;
    }
    // Check for title
    if (line1 === "") line1 = properties.title ? properties.title : "";
    // Chekc on line 1, if empty or only whitespace, dont insert any label, just return
    if (line1.trim() === "") return;

    // Check for activity
    let activity = false;
    if (properties.activityId && !properties.originFeature) activity = true;

    // Set label background opacity
    // Check if option overwrite is active
    let backgroundAlpha = transparent ? 0.5 : activity ? 0.5 : 0;
    if (
      this._labelBackgroundOpacity !== undefined &&
      this._labelBackgroundOpacity >= 0 &&
      this._labelBackgroundOpacity <= 1
    ) {
      backgroundAlpha = this._labelBackgroundOpacity!;
    }

    // --- Zoom level 2 --- //
    // Get species label
    const spcNameAndAbbre = this._getSpeciesNameAndAbbreviation(
      properties && properties.mainSpecies ? properties.mainSpecies : "",
    );

    let line2 = spcNameAndAbbre.abbreviation;
    // if (properties && properties.standIds && properties.standIds.length > 1) {
    //    line2 = line2;// + " ...";
    // }
    // Get year label
    let line3 = properties && properties.mainSpeciesYear ? properties.mainSpeciesYear : "";
    if (activity) {
      line2 = properties && properties.activityType ? properties.activityType : "";
      // Check if there are any overwrites for activity type
      if (overwrites?.labels && !overwrites.labels.showActivityType) {
        line2 = "";
      }
      line3 = beautifyNumber(properties.featureMetric, 2) + " ha";
    }

    // Label parameters
    const offsetYPx = activity ? 52 : 0;
    const z1 = this._polygonsLayerMinZoom + 6;
    const z2 = this._polygonsLayerMinZoom + 7;

    // IMPORTANT: LabelsMarker semantics are `mapZoom > zoomLevel`
    // so if you want a line to appear at zoom 16, set zoomLevel = 15.
    const baseFs = this._labelFontSize; // or whatever you used
    const bg = backgroundAlpha;

    const lines: CanvasLabelLine[] = [];
    // line1 always exists (or empty)
    if (line1) {
      lines.push({
        text: line1,
        zoomLevel: z1, // show when zoom > z1
        fontSizePx: baseFs,
        fontWeight: activity ? 700 : 400,
        color: "black",
      });
    }
    // zoom level 2 adds more detail (line2/line3).
    if (line2) {
      lines.push({
        text: line2,
        zoomLevel: z2,
        fontSizePx: baseFs,
        fontWeight: activity ? 400 : 700, // matches your old lcZoomLevel2 logic
        color: "black",
      });
    }
    if (line3) {
      lines.push({
        text: line3,
        zoomLevel: z2,
        fontSizePx: baseFs,
        fontWeight: 400,
        color: "black",
      });
    }
    // If labels are not zoom dependent, set all lines to the same zoom level (polygonsLayerMinZoom) so they show at the same time
    if (this._labelsLayerZoomDependent === false) {
      lines.forEach((line) => (line.zoomLevel = this._polygonsLayerMinZoom));
    }

    // minZoom: keep the old behavior (canvas layer checks zoom < row.minZoom first)
    const minZoom =
      this._labelsLayerZoomDependent === false ? this._polygonsLayerMinZoom : Math.min(z1, z2) + 1; // because display condition is zoom > zoomLevel

    // Find label placement
    // @ts-ignore
    if (layer.feature.geometry.type === "MultiPolygon") {
      // @ts-ignore
      layer.feature.geometry.coordinates.forEach((coords: Position[][], idx: number) => {
        const feat: Polygon = {
          type: "Polygon",
          coordinates: coords,
        };
        const placement = getPointInsideGeoJsonPolygon(feat);
        // IMPORTANT: if multiple parts share the same id, the last one wins.
        const partId = `${featureId}__${idx}`;
        createdIds.push(partId);
        // If you need *multiple* labels per MultiPolygon, we can change id scheme to `${id}__${idx}`
        this._polygonLabelCanvas.upsert(
          this._mkCanvasDatum({
            id: partId,
            placement,
            lines,
            minZoom,
            fontWeight: activity ? 700 : 400,
            backgroundAlpha: bg,
            offsetYPx,
          }),
        );
      });
    } else {
      // @ts-ignore
      const placement = getPointInsideGeoJsonPolygon(layer.feature.geometry);
      createdIds.push(featureId);
      this._polygonLabelCanvas.upsert(
        this._mkCanvasDatum({
          id: featureId,
          placement,
          lines,
          minZoom,
          fontWeight: activity ? 700 : 400,
          backgroundAlpha: bg,
          offsetYPx,
        }),
      );
    }
    this._registerPolygonCanvasLabelIds(featureId, createdIds);
  }

  private _reinsertLabels(layer: "points" | "lines" | "polygons") {
    if (layer === "points") {
      this._pointLabels.clearLayers();
      // @ts-ignore
      Object.values<L.Layer>(this._points._layers).forEach((layer) => {
        this._addPointLabel(layer);
      });
    } else if (layer === "lines") {
      this._lineLabelCanvas.clear();
      this._lineLabelIdsByFeatureId.clear();
      // @ts-ignore
      Object.values<L.Layer>(this._lines._layers).forEach((layer) => {
        this._addLineLabel(layer);
      });
    } else if (layer === "polygons") {
      // ✅ clear canvas + index
      this._polygonLabelCanvas.clear();
      this._polygonLabelIdsByFeatureId.clear();
      // rebuild labels for all current polygons
      // @ts-ignore
      Object.values<L.Layer>(this._polygons._layers).forEach((layer) => {
        this._addPolygonLabel(layer, { transparent: this._transparent });
      });
    } else {
      throw new Error("Error: Layer unknown in reinsertLabels");
    }
  }

  private _bindTooltipLayer(layer: L.GeoJSON | L.Layer, options?: { pane?: string }) {
    // Set options
    const pane = options?.pane || this._toolTipPane;
    try {
      // @ts-ignore
      if (!layer._layers) {
        let properties = null;
        // @ts-ignore
        if (layer.feature) {
          // @ts-ignore
          properties = layer.feature.properties;
        } else {
          // @ts-ignore
          properties = layer.options;
        }
        // @ts-ignore
        const text = this._getToolTipText(properties);
        if (text) layer.bindTooltip(text, { pane: pane, sticky: true });
        return;
      }
      // @ts-ignore
      Object.values(layer._layers).forEach((lay: L.Layer) => {
        // Get tooltip text
        // @ts-ignore
        if (lay.feature?.properties) {
          // @ts-ignore
          const text = this._getToolTipText(lay.feature.properties);
          // @ts-ignore
          if (text) lay.bindTooltip(text, { pane: pane, sticky: true });
        }
      });
    } catch (error) {
      console.warn("Not able to bindTooltip", { error, layer });
    }
  }

  private _unbindTooltipLayer(layer: L.GeoJSON | L.Layer): number[] {
    let layersWithTooltip: number[] = [];
    try {
      // @ts-ignore
      Object.values(layer._layers).forEach((lay: L.Layer) => {
        // @ts-ignore
        layersWithTooltip.push(lay._leaflet_id as number);
        // @ts-ignore
        unbindLayerTooltip(lay);
      });
    } catch (error) {
      console.warn("Not able to unbindTooltip", { error, layer });
    }
    return layersWithTooltip;
  }

  private _closeTooltipLayer(layer: L.GeoJSON | L.Layer): void {
    try {
      // @ts-ignore
      Object.values(layer._layers).forEach((lay: L.Layer) => lay.closeTooltip());
    } catch (error) {
      console.warn("Not able to closeTooltip", { error, layer });
    }
  }

  private _getToolTipText(properties: GeoJsonProperties) {
    if (!properties) return null;
    // Point feature
    if (properties.type === "Point" || properties.type === "MultiPoint") {
      let title = properties.section ? properties.section.join("") : "";
      if (properties.title) title = properties.title;
      if (title === "") return null;
      return "<b>" + title + "</b><br>" + truncLongTextToHtml(properties.note!);
    }
    // General data
    const headerText =
      properties.section && properties.section.join("") !== ""
        ? properties.section.join("")
        : properties.title
        ? properties.title
        : "";
    const header = "<b>" + headerText + "</b><br>";
    let label = "";
    let metricText = "";
    let multiSpecies = "";
    let speciesText = "";
    let yearText = "";
    let volumeText = "";
    // Feature metric (line length or polygon area)
    if (properties.type === "LineString" || properties.type === "MultiLineString") {
      // Line feature
      label = `<b>${this._translations.type} </b>`;
      if (properties.featureMetric) {
        metricText =
          `<b>${this._translations.length} </b>` +
          beautifyNumber(properties.featureMetric, 2) +
          " m<br>";
      }
    } else {
      // Polygon feature
      label = `<b>${this._translations.species} </b>`;
      if (properties.featureMetric) {
        metricText =
          `<b>${this._translations.area} </b>` +
          beautifyNumber(properties.featureMetric, 2) +
          " ha<br>";
      }
    }
    // Check for multi species and set species text
    const spcNameAndAbbre = this._getSpeciesNameAndAbbreviation(properties.mainSpecies);
    if (properties.standIds && properties.standIds.length > 1) {
      // multiSpecies = " ..."
      multiSpecies = "";
    }
    if (properties.mainSpecies) {
      const lab =
        properties.mainSpecies === TBD_SPECIES
          ? spcNameAndAbbre.name
          : spcNameAndAbbre.abbreviation;
      speciesText = label + lab + multiSpecies + "<br>";
    }
    // Check for main species year and volume
    if (properties.mainSpeciesYear) {
      yearText =
        `<b>${this._translations.year} </b>` +
        properties.mainSpeciesYear +
        " (" +
        (dayjs().year() - properties.mainSpeciesYear) +
        ")<br>";
    }
    if (properties.mainSpeciesVolume) {
      volumeText =
        `<b>${this._translations.volume} </b>` +
        beautifyNumber(properties.mainSpeciesVolume) +
        " m3<br>";
    }

    return header + metricText + speciesText + yearText + volumeText;
  }
  // --- Style functions --- //
  private _getLineFeatureStyle(feature: any) {
    const { properties } = feature;
    const { mainSpecies, styleObject } = properties;
    // Return style accoring to program defined functionality
    return this._getLineStyle(mainSpecies, styleObject);
  }

  private _getMarkerIcon(featureId: string, iconId: string, text?: string, color?: string) {
    // Check if feature id is in filter values
    // let textBgColor = "rgba(0, 0, 0, 0.0)"; // 0,5
    let textColor = "black"; //"white";
    let iconOpacity = 1.0;
    let iconStrokeOpacity = 0.7;
    if (this._filterIds) {
      if (!this._filterIds.includes(featureId)) {
        // textBgColor = "rgba(0, 0, 0, 0.0)"; // 0,2
        // textColor = "#a1a1a1"
        iconStrokeOpacity = 0.2;
        iconOpacity = 0.5;
      }
    }
    // Set background color if option is set
    // Check if option overwrite is active
    let backgroundAlpha = 0;
    if (
      this._labelBackgroundOpacity !== undefined &&
      this._labelBackgroundOpacity >= 0 &&
      this._labelBackgroundOpacity <= 1
    ) {
      backgroundAlpha = this._labelBackgroundOpacity!;
    }
    //const newColor = color ? color : this._darkTheme ? 'black' : '#e1e1e1';
    if (iconId.toLowerCase() === "text") {
      return L.divIcon({
        className: "text-icon",
        html: `<p style="font-family: sans-serif; font-size:${
          this._textPointFontSize
        }px; white-space: nowrap;"><span style="color: ${textColor}; background-color: rgba(255, 255, 255, ${backgroundAlpha}); font-weight: 800; padding: 0 2px; border-radius: 2px">${
          text ? text : ""
        }</span></p>`,
        iconAnchor: [0, 18],
      });
    }
    // Form svg style object
    const styObj: SvgIconStyleObject = {
      fillColor: `rgba(0, 0, 0, ${iconOpacity})`,
      strokeColor: `rgba(255, 255, 255, ${iconStrokeOpacity})`,
      strokeWidth: this._darkTheme ? 0 : 1.3,
    };
    const iconHtml = this._getIcon(iconId as IconID, styObj);
    if (iconHtml) {
      return L.divIcon({
        // @ts-ignore
        className: {},
        iconSize: [24, 24],
        html: iconHtml,
        iconAnchor: [12, 12],
      });
    }
    return new L.Icon.Default();
  }

  private _resetFeatureGroupStyle(group: FeatureGroup) {
    //@ts-expect-error
    if (group._layers) {
      //@ts-expect-error
      Object.values(group._layers).forEach((lay: L.Layer) => {
        //@ts-expect-error
        if (["LineString", "MultiLineString"].includes(lay.options.type)) {
          // @ts-expect-error
          lay.setStyle({
            // @ts-expect-error
            color: lay.options.originalColor
              ? // @ts-expect-error
                lay.options.originalColor
              : // @ts-expect-error
                this._getSpeciesColor(lay.options.mainSpecies),
          });
        }
      });
    }
  }
  private _getSelectedLineStyle(feature: any) {
    // Check for transparent mode
    if (this._transparent) {
      return {
        color: feature.options.originalColor,
      };
    }
    return {
      opacity: 1,
      color: lightenDarkenColor(feature.options.color, -30),
    };
  }
  private _getSelectedPolygonStyle(feature: any) {
    const pane = this._getPolygonPane(feature);
    return {
      color: feature.options.fillColor,
      weight: this._polygonSelectedLineWeight,
      fillOpacity: this._polygonSelectedFillOpacity,
      dashArray: this._polygonSelectedDashArray,
      pane: pane,
    };
  }
  private _getFeatureNotInFilterStyle(feature: any) {
    const pane = this._getPolygonPane(feature);
    // If transparent mode active
    if (this._transparent) {
      return {
        color: this._darkTheme ? "black" : "white",
        fillOpacity: 0,
        pane,
      };
    }
    // If normal mode
    return {
      fillOpacity: this._notInFilterOpacity,
      pane,
    };
  }
  private _getTransparentStyle(feature?: any) {
    // check for line feature
    if (
      feature &&
      feature.options &&
      feature.options.type &&
      ["LineString", "MultiLineString"].includes(feature.options.type)
    ) {
      return this._getLineTransparentStyle();
    }
    let pattern = null;
    if (feature) {
      pattern = this._getPattern(feature);
    }
    const pane = this._getPolygonPane(feature || {});
    return {
      color: this._darkTheme ? "black" : "white",
      dashArray: "1,0",
      fillOpacity: pattern ? 0.7 : 0,
      weight: 3,
      fillPattern: pattern,
      pane: pane,
    };
  }
  private _getLineTransparentStyle() {
    return {
      color: this._darkTheme ? "black" : "white",
    };
  }
  private _resetGlobalFeatureStyle(feature: L.Layer) {
    let feat = feature;
    if (feature) {
      // @ts-ignore
      if (feature.feature) {
        // @ts-ignore
        feat = feature.feature;
      } else {
        // @ts-ignore
        feat = feature.toGeoJSON();
        // @ts-ignore
        feat.properties = { ...feature.options };
      }
    }
    // @ts-ignore
    if (feat && !["Point", "MultiPoint"].includes(feat.geometry.type) && feature._eventParents) {
      // First reset all feature groups styles
      // @ts-ignore
      // @ts-ignore
      for (const key in feature._eventParents) {
        // @ts-ignore
        const parentOptions = feature._eventParents[key].options;
        // Check for transparent mode
        if (this._transparent) {
          // @ts-ignore
          Object.values(feature._eventParents[key]._layers).forEach((lay: L.GeoJSON) => {
            lay.setStyle(this._getTransparentStyle(lay.feature));
          });
          // @ts-ignore
          // feature._eventParents[key].setStyle(this._getTransparentStyle(feature));
        } else {
          // @ts-ignore
          if (feature._eventParents[key].options && feature._eventParents[key].options.style) {
            // @ts-ignore
            feature._eventParents[key].resetStyle();
          } else {
            // @ts-ignore
            this._resetFeatureGroupStyle(feature._eventParents[key]);
            // @ts-ignore
            // feature._eventParents[key].setStyle(this._getFeatureNotInFilterStyle());
          }
        }
        // Check for other stuff such as in filter
        if (this._filterIds) {
          // HACK : This is a hack to disable the filter on project layer.
          // This is needed because the object only knows its own filter values and not the parents
          // therefore, the project layer will be filtered out if the parent is filtered out
          // Posible solution: The issues stems from dividing the map layers and project layers into
          // different instances of the layerController where GeoJsonLayerGroup is used.
          // This could possibly be solved by having a single instance of the layerController with
          // all layers in it.
          if (parentOptions.pane && parentOptions.pane.startsWith("project")) return;
          // @ts-ignore
          Object.values(feature._eventParents[key]._layers).forEach((lay: L.GeoJSON) => {
            if (
              // @ts-expect-error
              lay.feature?.properties?.id &&
              // @ts-expect-error
              !this._filterIds?.includes(lay.feature?.properties?.id)
            ) {
              if (this._transparent) {
                lay.setStyle(this._getTransparentStyle(lay));
              } else {
                lay.setStyle(this._getFeatureNotInFilterStyle(lay));
              }
            }
          });
        }
      }
    }
  }

  private _sampleLineByDistance(
    latlngs: L.LatLng[],
    opts: {
      maxLabels: number; // hard cap
      minSpacingM?: number; // world-space spacing
      minSpacingPx?: number; // screen-space spacing (requires map)
      map?: L.Map; // required if minSpacingPx is used
      seed?: string; // optional seed to randomize label positions (e.g. per feature) while keeping them stable across runs
    },
  ): { latlng: L.LatLng; angleRad: number }[] {
    const maxLabels = Math.max(0, opts.maxLabels | 0);
    if (maxLabels === 0) return [];
    if (latlngs.length < 2) return [];

    // total length in meters
    let totalM = 0;
    for (let i = 0; i < latlngs.length - 1; i++) totalM += latlngs[i].distanceTo(latlngs[i + 1]);
    if (totalM <= 0) return [];

    // derive an effective spacing in meters
    let spacingM = opts.minSpacingM ?? 0;

    // if caller wants minSpacingPx, convert to meters at current zoom (local approximation)
    if (opts.minSpacingPx && opts.map) {
      const m0 = opts.map.latLngToContainerPoint(latlngs[0]);
      const m1 = L.point(m0.x + opts.minSpacingPx, m0.y);
      const ll1 = opts.map.containerPointToLatLng(m1);
      const pxSpacingM = latlngs[0].distanceTo(ll1);
      spacingM = Math.max(spacingM, pxSpacingM);
    }

    // decide count
    // - start from hard cap
    // - if spacingM is set, cap further by length/spacing
    let count = maxLabels;
    if (spacingM > 0) {
      // count = Math.min(count, Math.floor(totalM / spacingM));
      const bySpacing = Math.floor(totalM / spacingM);
      // allow 1 label even if totalM < spacingM
      count = Math.min(count, Math.max(1, bySpacing));
    }

    // still allow 1 label if line is long enough for at least one spacing unit,
    // OR if you want "always at least 1 when visible", remove this guard.
    if (count <= 0) return [];

    // distribute inside the line, excluding endpoints
    const step = totalM / (count + 1);
    const phase = opts.seed ? hash01(opts.seed) * step : 0;
    const targets = Array.from({ length: count }, (_, k) => phase + step * (k + 1)).filter(
      (t) => t > 0 && t < totalM,
    ); // safety filter, should not be needed if count is correct
    // const targets = Array.from({ length: count }, (_, k) => step * (k + 1));

    const out: { latlng: L.LatLng; angleRad: number }[] = [];
    let acc = 0;
    let tIdx = 0;

    for (let i = 0; i < latlngs.length - 1 && tIdx < targets.length; i++) {
      const a = latlngs[i];
      const b = latlngs[i + 1];
      const segLen = a.distanceTo(b);
      if (segLen <= 0) continue;

      while (tIdx < targets.length && acc + segLen >= targets[tIdx]) {
        const distIntoSeg = targets[tIdx] - acc;
        const r = distIntoSeg / segLen;

        const lat = a.lat + (b.lat - a.lat) * r;
        const lng = a.lng + (b.lng - a.lng) * r;
        const angleRad = Math.atan2(b.lat - a.lat, b.lng - a.lng);

        out.push({ latlng: L.latLng(lat, lng), angleRad });
        tIdx++;
      }
      acc += segLen;
    }

    return out;
  }

  private _getPattern(feature: Feature) {
    // Check for feature data
    if (
      !feature ||
      !feature.properties ||
      !feature.properties.categories ||
      feature.properties.categories.length === 0
    ) {
      return null;
    }
    const featCategories = feature.properties.categories as unknown as string[];
    // Get common categories
    const comCat = getCommonElements(
      SPECIAL_STYLE_CATEGORIES as unknown as string[],
      featCategories,
    );
    // Get category overwrite styles
    const overwrites = this._getOverwrites ? this._getOverwrites() : undefined;
    // If no common categories return null
    if ((!overwrites || !overwrites.categories) && comCat.length === 0) return null;
    const { id, mainSpecies, styleObject } = feature.properties!;
    let cat = comCat[0] ? (comCat[0] as SPECIAL_STYLE_CATEGORIES_TYPE) : null;
    // Find potential category overwrites
    let patternOverwrite = undefined;
    if (overwrites && overwrites.categories) {
      for (const category of featCategories) {
        if (overwrites.categories[category]) {
          patternOverwrite = overwrites.categories[category].style;
          cat = category as SPECIAL_STYLE_CATEGORIES_TYPE;
          break;
        }
      }
    }
    if (!cat) return null;
    // Set pattern deciding category to first element
    const styObj = styleObject ? styleObject : null;
    if (this._transparent) {
      // @ts-ignore
      const pattern = Object.values(this._map._patterns).find(
        (p: any) => p.options.id === id && p.options.transparent && p.options.type === cat,
      );
      if (pattern) {
        return pattern;
      }
      // Crate new pattern if it does not yet exist
      const newPattern = createNewPattern({
        featureId: id,
        transparent: this._transparent,
        color: this._getSpeciesColor(mainSpecies, styObj),
        category: cat,
        patternOverwrite,
      });
      if (newPattern) newPattern.addTo(this._map);
      return newPattern;
    } else {
      // Check if pattern already exist
      // @ts-ignore
      const pattern = Object.values(this._map._patterns).find((p: any) => {
        return (
          p.options.id === id &&
          !p.options.transparent &&
          p.options.type === cat &&
          p.options.orgColor === this._getSpeciesColor(mainSpecies, styObj)
        );
      });
      if (pattern) { 
        return pattern;
      } 
      // Crate new pattern if it does not yet exist
      const newPattern = createNewPattern({
        featureId: id,
        transparent: this._transparent,
        color: this._getSpeciesColor(mainSpecies, styleObject),
        category: cat,
        patternOverwrite,
      });
      if (newPattern) newPattern.addTo(this._map);
      return newPattern;
    }
  }

  // --- Add/Remove feature from layer --- //
  private _addFeatureToPointLayer(feature: Feature) {
    const id = feature.properties!.id;
    // Check if feature already exist
    removeFeatureFromLayerGroup(this._points, id);
    this._removePointCanvasLabelsForFeatureId(id);
    this._points.addData(feature);
  }

  private _addFeatureToLineLayer(feature: Feature) {
    const id = feature.properties!.id;
    // Check if line is area bearing and place in corridor layer
    removeFeatureFromLayerGroup(this._lines, id);
    this._removeLineCanvasLabelsForFeatureId(id);
    this._addFeatureToLinesLayer(feature);
  }

  private _addFeatureToPolygonLayer(feature: Feature) {
    const id = feature.properties!.id;
    // Check if feature already exist
    removeFeatureFromLayerGroup(this._polygons, id);
    this._removePolygonCanvasLabelsForFeatureId(id);
    this._polygons.addData(feature);
  }

  private _removeFeatureFromPointLayer(featureId: string) {
    let removed: boolean = false;
    removed = removeFeatureFromLayerGroup(this._points, featureId);
    this._removePointCanvasLabelsForFeatureId(featureId);
    return removed;
  }

  private _removeFeatureFromLineLayer(featureId: string) {
    let removed: boolean = false;
    removed = removeFeatureFromLayerGroup(this._lines, featureId);
    removed = removeFeatureFromLayerGroup(this._lineBuffers, featureId);
    this._removeLineCanvasLabelsForFeatureId(featureId);
    return removed;
  }

  private _removeFeatureFromPolygonLayer(featureId: string) {
    let removed: boolean = false;
    removed = removeFeatureFromLayerGroup(this._polygons, featureId);
    this._removePolygonCanvasLabelsForFeatureId(featureId);
    return removed;
  }
}

export default GeoJsonLayerGroup;
